<template>
  <v-card id="acceptance-history" class="pa-4 mb-4">
    <div class="text-subtitle-1 font-weight-medium mb-2">
      {{ $t("acceptance.history.title") }}
    </div>

    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      density="compact"
      class="mb-2"
    >
      {{ $t("acceptance.errors.events_failed") }}
    </v-alert>

    <v-table v-if="events.length" density="comfortable">
      <thead>
        <tr>
          <th class="text-left">{{ $t("acceptance.history.when") }}</th>
          <th class="text-left">{{ $t("acceptance.history.event") }}</th>
          <th class="text-left">{{ $t("acceptance.history.who") }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="event in events"
          :id="`acceptance-event-${event.id}`"
          :key="event.id"
          class="acceptance-history-row"
          :class="{ 'acceptance-focus': event.id === focusEventId }"
        >
          <td class="text-no-wrap">{{ formatTime(event.recordedAt) }}</td>
          <td>
            <div class="font-weight-medium">{{ summary(event) }}</div>
            <div
              v-for="(line, index) in details(event)"
              :key="index"
              class="text-caption"
            >
              {{ line }}
            </div>
            <div v-if="event.reason" class="text-caption text-grey-darken-1">
              {{ $t("acceptance.history.reason", { reason: event.reason }) }}
            </div>
          </td>
          <td>{{ who(event) }}</td>
        </tr>
      </tbody>
    </v-table>
    <div v-else-if="!loading && !error" class="text-body-2 text-grey-darken-1">
      {{ $t("acceptance.history.empty") }}
    </div>
    <v-skeleton-loader v-if="loading && !events.length" type="text, text" />

    <v-pagination
      v-if="pageCount > 1"
      class="mt-2"
      density="comfortable"
      :model-value="page"
      :length="pageCount"
      :total-visible="7"
      :disabled="loading"
      @update:model-value="load"
    />
  </v-card>
</template>

<script>
import moment from "moment";
import { getDossierEvents } from "@/api/compliance";
import { formatCivilDate } from "@/components/Acceptance/deadlines.mjs";
import { changeLine } from "@/components/Acceptance/corrections.mjs";
import { praChangeLine } from "@/components/Acceptance/praCorrections.mjs";
import { holdTypeKey } from "@/components/Acceptance/praHoldForm.mjs";
import {
  displayText,
  workshopChangeLine,
} from "@/components/Workshop/workshopCorrections.mjs";

// Events per page, newest first (the API allows up to 100).
const PAGE_SIZE = 10;

// These events list each change as "Field: from → to" (dossier.started: the VIN and plate
// prefilled from the car; pra.corrected with the PRA field labels; workshop.corrected with the
// task card labels).
const DETAILED_EVENTS = [
  "dossier.started",
  "dossier.corrected",
  "dossier.updatedAfterConfirm",
  "pra.corrected",
  "workshop.corrected",
];

// API event type -> acceptance.history.events.<key> (locale keys are snake_case, without dots).
const EVENT_KEYS = {
  "dossier.started": "started",
  "dossier.draftSaved": "draft_saved",
  "weighing.added": "weighing_added",
  "file.added": "file_added",
  "file.removed": "file_removed",
  "dossier.confirmed": "confirmed",
  "dossier.updatedAfterConfirm": "updated_after_confirm",
  "dossier.corrected": "corrected",
  "pra.caseOpened": "pra_case_opened",
  "pra.crdRecorded": "pra_crd_recorded",
  "pra.requestRecorded": "pra_request_recorded",
  "pra.receiptRecorded": "pra_receipt_recorded",
  "pra.fileReplaced": "pra_file_replaced",
  "pra.fileRemoved": "pra_file_removed",
  "pra.holdPlaced": "pra_hold_placed",
  "pra.holdReleased": "pra_hold_released",
  "pra.corrected": "pra_corrected",
  "workshop.checklistOpened": "workshop_checklist_opened",
  "workshop.resultRecorded": "workshop_result_recorded",
  "workshop.resultRedone": "workshop_result_redone",
  "workshop.exceptionAccepted": "workshop_exception_accepted",
  "workshop.approved": "workshop_approved",
  "workshop.corrected": "workshop_corrected",
  // Stage 6 slice 4: the waste events that carry the dossier.
  "waste.entryRecorded": "waste_entry_recorded",
  "waste.entryReversed": "waste_entry_reversed",
  "waste.carBodyReleased": "waste_car_body_released",
  "waste.carBodyReleaseCancelled": "waste_car_body_release_cancelled",
};

// First segment of a changed field -> acceptance.history.sections.<key>
const SECTION_KEYS = {
  arrival: "arrival",
  identity: "identity",
  owner: "owner",
  deliverer: "deliverer",
  authorityToDeliver: "authority",
  checklist: "checklist",
  hazards: "hazards",
  quarantine: "quarantine",
};

export default {
  name: "AcceptanceHistoryCard",
  props: {
    dossierId: { type: String, required: true },
    // The page increments it after each save to reload the log.
    refreshKey: { type: Number, default: 0 },
    // An event id to highlight (the page sets it when "Valore precedente" is followed).
    focusEventId: { type: String, default: null },
  },
  data: () => ({
    events: [],
    count: 0,
    page: 1,
    loading: false,
    error: false,
  }),
  computed: {
    pageCount() {
      return Math.max(1, Math.ceil(this.count / PAGE_SIZE));
    },
  },
  watch: {
    refreshKey() {
      this.load(1);
    },
    dossierId() {
      this.load(1);
    },
    focusEventId(id) {
      if (id) this.showEvent(id);
    },
  },
  created() {
    // Only the latest request may fill the table (a save can reload while a page is loading).
    this.loadSeq = 0;
  },
  mounted() {
    this.load(1);
  },
  methods: {
    // One page of the log; the rows of the previous page stay visible until it arrives. A save
    // reloads page 1, where its new entry is.
    async load(page) {
      const seq = (this.loadSeq += 1);
      this.loading = true;
      this.error = false;
      try {
        const result = await getDossierEvents(this.dossierId, page, PAGE_SIZE);
        if (seq !== this.loadSeq) return;
        this.events = result.events;
        this.count = result.count;
        this.page = page;
      } catch (err) {
        if (seq === this.loadSeq) this.error = true;
      } finally {
        if (seq === this.loadSeq) this.loading = false;
      }
    },
    // "Valore precedente" points at a log entry that may be on another page: look for it page by
    // page from the newest, show that page and scroll to the row. Not found: the page stays.
    async showEvent(id) {
      if (this.events.some((event) => event.id === id)) return;
      const seq = (this.loadSeq += 1);
      this.loading = true;
      try {
        for (let page = 1; page <= this.pageCount; page += 1) {
          const result = await getDossierEvents(
            this.dossierId,
            page,
            PAGE_SIZE,
          );
          if (seq !== this.loadSeq) return;
          if (result.events.some((event) => event.id === id)) {
            this.events = result.events;
            this.count = result.count;
            this.page = page;
            this.$nextTick(() => {
              const row = document.getElementById(`acceptance-event-${id}`);
              if (row)
                row.scrollIntoView({ behavior: "smooth", block: "center" });
            });
            return;
          }
        }
      } catch (err) {
        if (seq === this.loadSeq) this.error = true;
      } finally {
        if (seq === this.loadSeq) this.loading = false;
      }
    },
    // "Targa: DG853SV → AB123CD", one per changed field.
    details(event) {
      if (!DETAILED_EVENTS.includes(event.type)) return [];
      let lineOf = event.type === "pra.corrected" ? praChangeLine : changeLine;
      if (event.type === "workshop.corrected") {
        lineOf = (change) =>
          workshopChangeLine(change, { locale: this.$i18n.locale });
      }
      return (event.changes || [])
        .map(lineOf)
        .filter(Boolean)
        .map((line) => {
          let label = this.$t(line.label);
          if (line.lineCode) {
            const key = `acceptance.checklist.lines.${line.lineCode}`;
            const name = this.$t(key) === key ? line.lineCode : this.$t(key);
            label = `${name} · ${label}`;
          }
          return `${label}: ${this.shown(line.from)} → ${this.shown(line.to)}`;
        });
    },
    shown(display) {
      return displayText(display, (key) => this.$t(key));
    },
    formatTime(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "";
    },
    who(event) {
      const actor = event.actor || {};
      const name = actor.name || "—";
      return actor.role ? `${name} (${actor.role})` : name;
    },
    summary(event) {
      const eventKey = EVENT_KEYS[event.type];
      const title = eventKey
        ? this.$t(`acceptance.history.events.${eventKey}`)
        : event.type;
      if (event.type === "weighing.added")
        return this.weighingSummary(title, event);
      if (event.type === "file.added" || event.type === "file.removed")
        return this.fileSummary(title, event);
      if (event.type === "dossier.confirmed")
        return this.confirmedSummary(title, event);
      if (event.type === "pra.crdRecorded")
        return this.changeSummary(title, event, "crd.number");
      if (event.type === "pra.requestRecorded")
        return this.requestSummary(title, event);
      if (event.type === "pra.receiptRecorded")
        return this.changeSummary(title, event, "receipt.number");
      if (event.type === "pra.fileReplaced" || event.type === "pra.fileRemoved")
        return this.praFileSummary(title, event);
      if (event.type === "pra.holdPlaced")
        return this.holdSummary(title, event);
      if (
        event.type === "workshop.resultRecorded" ||
        event.type === "workshop.resultRedone" ||
        event.type === "workshop.exceptionAccepted" ||
        event.type === "workshop.corrected"
      )
        return this.workshopResultSummary(title, event);
      if (event.type === "workshop.approved")
        return this.workshopApprovedSummary(title, event);
      if (
        event.type === "waste.entryRecorded" ||
        event.type === "waste.entryReversed"
      )
        return this.changeSummary(title, event, "entry.container");
      if (event.type === "waste.carBodyReleased")
        return this.changeSummary(title, event, "release.container");
      if (event.type === "waste.carBodyReleaseCancelled")
        return event.reason ? `${title} · ${event.reason}` : title;
      const sections = [];
      (event.changes || []).forEach((change) => {
        const section = SECTION_KEYS[String(change.field).split(".")[0]];
        if (section && !sections.includes(section)) sections.push(section);
      });
      if (!sections.length) return title;
      const names = sections.map((section) =>
        this.$t(`acceptance.history.sections.${section}`),
      );
      return `${title} · ${names.join(", ")}`;
    },
    // "File aggiunto · Documenti › Targhe" from the checklist.<code>.fileIds change, or
    // "File rimosso · Foto › VIN" from photos.<kind> (file.added and file.removed).
    fileSummary(title, event) {
      const fields = (event.changes || []).map((change) =>
        String(change.field),
      );
      let photo = null;
      let line = null;
      let workshop = null;
      fields.forEach((field) => {
        photo = photo || /^photos\.([^.]+)$/.exec(field);
        line = line || /^checklist\.(.+)\.fileIds$/.exec(field);
        workshop = workshop || /^workshop\.(.+)\.fileIds$/.exec(field);
      });
      // A workshop evidence file: "File aggiunto · Bonifica › Refrigerante A/C".
      if (workshop) {
        const key = `workshop.task.${workshop[1]}`;
        const label = this.$t(key) === key ? workshop[1] : this.$t(key);
        return `${title} · ${this.$t("acceptance.history.file_targets.workshop")} › ${label}`;
      }
      if (photo) {
        const kind = this.$t(`acceptance.photos.kinds.${photo[1]}`);
        return `${title} · ${this.$t("acceptance.history.file_targets.photos")} › ${kind}`;
      }
      if (line) {
        const key = `acceptance.checklist.lines.${line[1]}`;
        const label = this.$t(key) === key ? line[1] : this.$t(key);
        return `${title} · ${this.$t("acceptance.history.file_targets.checklist")} › ${label}`;
      }
      return title;
    },
    // "Accettazione confermata · bonifica 05/11/2026 · radiazione 24/11/2026" from the
    // deadlines.* changes.
    confirmedSummary(title, event) {
      const due = (field) => {
        const change = (event.changes || []).find((c) => c.field === field);
        return change ? formatCivilDate(change.to) : "";
      };
      const depollution = due("deadlines.depollution");
      const deregistration = due("deadlines.deregistrationRequest");
      if (!depollution || !deregistration) return title;
      return `${title} · ${this.$t("acceptance.history.confirmed_deadlines", { depollution, deregistration })}`;
    },
    // "Bonifica approvata · nei tempi" (or "in ritardo") from the approval.onTime change; without a
    // due date (onTime null) the title alone.
    workshopApprovedSummary(title, event) {
      const change = (event.changes || []).find(
        (c) => c.field === "approval.onTime",
      );
      if (!change || typeof change.to !== "boolean") return title;
      const timing = change.to ? "on_time" : "late";
      return `${title} · ${this.$t(`workshop.checklist.timing.${timing}`)}`;
    },
    // "Risultato bonifica registrato · Olio motore › Eseguita" from the result.taskCode and
    // result.result changes; a redo reads "Risultato bonifica ripetuto · …" the same way, and an
    // accepted exception "Eccezione accettata · Refrigerante A/C" (no result change).
    workshopResultSummary(title, event) {
      const value = (field) => {
        const change = (event.changes || []).find((c) => c.field === field);
        return change ? change.to : null;
      };
      const task = value("result.taskCode");
      if (!task) return title;
      const taskKey = `workshop.task.${task}`;
      const parts = [this.$t(taskKey) === taskKey ? task : this.$t(taskKey)];
      const result = value("result.result");
      if (result) parts.push(this.$t(`workshop.result.${result}`));
      return `${title} · ${parts.join(" › ")}`;
    },
    // "CRD registrato · CRD 2026 0412", "Ricevuta registrata · RAD-77120": the recorded value of
    // one change.
    changeSummary(title, event, field) {
      const change = (event.changes || []).find((c) => c.field === field);
      return change && change.to ? `${title} · ${change.to}` : title;
    },
    // "Richiesta di radiazione registrata · Agenzia STA" from the request.channel change.
    requestSummary(title, event) {
      const change = (event.changes || []).find(
        (c) => c.field === "request.channel",
      );
      if (!change || !change.to) return title;
      const key = `acceptance.pra.request.channels.${change.to}`;
      const channel = this.$t(key) === key ? change.to : this.$t(key);
      return `${title} · ${channel}`;
    },
    // "File sostituito · CRD · CRD_RVFU.pdf → CRD_corretto.pdf" from the <step>.fileName change
    // (pra.fileReplaced and pra.fileRemoved; the reason shows below).
    praFileSummary(title, event) {
      const change = (event.changes || []).find((c) =>
        /^(crd|request|receipt|hold)\.fileName$/.test(String(c.field)),
      );
      if (!change) return title;
      const step = String(change.field).split(".")[0];
      const stepKey = `acceptance.pra.file_steps.${step}`;
      const names = change.to
        ? `${change.from || "—"} → ${change.to}`
        : change.from || "—";
      return `${title} · ${this.$t(stepKey)} · ${names}`;
    },
    // "Pratica in sospeso · Fermo fiscale" from the activeHold.type change (pra.holdPlaced). The
    // release ("Sospensione rilasciata") shows its reason below, like a file change.
    holdSummary(title, event) {
      const change = (event.changes || []).find(
        (c) => c.field === "activeHold.type",
      );
      if (!change || !change.to) return title;
      const key = holdTypeKey(change.to);
      return `${title} · ${key ? this.$t(key) : change.to}`;
    },
    // "Pesatura aggiunta · netto 1.560 kg" from the weighings.<id>.net change.
    weighingSummary(title, event) {
      const net = (event.changes || []).find((change) =>
        /^weighings\.[^.]+\.net$/.test(String(change.field)),
      );
      if (!net || typeof net.to !== "number") return title;
      const kg = new Intl.NumberFormat(this.$i18n.locale, {
        maximumFractionDigits: 1,
      }).format(net.to);
      return `${title} · ${this.$t("acceptance.history.weighing_net", { net: `${kg} kg` })}`;
    },
  },
};
</script>

<style scoped>
/* Each entry starts at the top of its row with room above and below, so multi-line corrections
   do not run into the next entry. */
.v-table .acceptance-history-row > td {
  vertical-align: top;
  padding-top: 12px;
  padding-bottom: 12px;
}
.acceptance-focus {
  background-color: #fff8e1;
}
</style>
